import { Plus, Trash2, CornerDownRight } from 'lucide-react';
import { useId, useMemo } from 'react';
import type {
  FilterCondition,
  FilterFieldMeta,
  FilterGroup,
  FilterNode,
  FilterOperator,
} from '@aurora/contracts';
import { CURRENT_ACTOR_GROUPS_TOKEN, CURRENT_ACTOR_TOKEN, isFilterGroup } from '@aurora/contracts';
import { cn } from '../../lib/cn';
import { Button, Select } from '../../components/ui';
import { isConditionComplete, LIST_OPERATORS, VALUELESS } from './list-filters';
import { describeRolling, RELATIVE_LABELS, ValueEditor, type ReferenceLabeller } from './list-filter-values';

/**
 * The filter builder.
 *
 * Every field, every operator and every choice list comes from the server's
 * `/tickets/filters` endpoint, which is generated from the same schema the
 * compiler validates against. That is the point: the interface cannot offer a
 * filter the server would reject, and a new field appears here the moment an
 * administrator creates it — with no frontend change at all.
 *
 * This is a controlled editor: it reports every edit, complete or not. The
 * ticket list holds those edits as a draft and applies them only once each
 * condition is complete (see `ListFilterBuilder`); the administration forms
 * keep the conditions they are building in their own state.
 */

export const OPERATOR_LABELS: Record<FilterOperator, string> = {
  is: 'is',
  is_not: 'is not',
  in: 'is any of',
  not_in: 'is none of',
  contains: 'contains',
  not_contains: 'does not contain',
  starts_with: 'starts with',
  ends_with: 'ends with',
  gt: 'is greater than',
  gte: 'is at least',
  lt: 'is less than',
  lte: 'is at most',
  between: 'is between',
  before: 'is before',
  after: 'is after',
  on: 'is on',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
  has_any: 'has any of',
  has_all: 'has all of',
  has_none: 'has none of',
};

/** Fields a new condition starts on, when the server offers them: what people filter by most. */
const PREFERRED_START = ['status', 'priority', 'assignee_id', 'group_id'];

export interface FilterPanelProps {
  fields: FilterFieldMeta[];
  value: FilterGroup;
  onChange: (next: FilterGroup) => void;
  /**
   * Mark conditions that are not finished yet, and say why. For screens that
   * hold back incomplete conditions rather than sending them.
   */
  flagIncomplete?: boolean;
  /**
   * Which fields a condition may be switched to. A condition already on a field
   * this refuses keeps showing it, so a saved filter is never silently changed.
   */
  offerField?: (field: FilterFieldMeta) => boolean;
}

export function FilterPanel({ fields, value, onChange, flagIncomplete = false, offerField }: FilterPanelProps) {
  const fieldsByKey = useMemo(() => new Map(fields.map((field) => [field.field, field])), [fields]);
  const offered = useMemo(() => (offerField ? fields.filter(offerField) : fields), [fields, offerField]);

  const grouped = useMemo(() => groupFields(offered), [offered]);

  return (
    <GroupEditor
      group={value}
      offered={offered}
      grouped={grouped}
      fieldsByKey={fieldsByKey}
      onChange={onChange}
      depth={0}
      flagIncomplete={flagIncomplete}
    />
  );
}

function groupFields(fields: FilterFieldMeta[]): Array<[string, FilterFieldMeta[]]> {
  const groups = new Map<string, FilterFieldMeta[]>();
  for (const field of fields) {
    const key = field.group ?? 'Other';
    const list = groups.get(key) ?? [];
    list.push(field);
    groups.set(key, list);
  }
  return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
}

/**
 * The value a fresh condition starts with: only where one answer is a sensible
 * guess — "Yes", "Today". Anything else starts empty and is marked as needing
 * a choice; the builder used to start every condition on `''`, which the
 * server read as ticket 0, or rejected outright for a date.
 */
export function defaultValueFor(meta: FilterFieldMeta | undefined, op: FilterOperator): unknown {
  if (!meta || VALUELESS.includes(op)) return undefined;
  if (meta.type === 'boolean') return true;
  if ((meta.type === 'date' || meta.type === 'datetime') && op !== 'between') return { relative: 'today' };
  return undefined;
}

export function newCondition(fields: FilterFieldMeta[]): FilterCondition | null {
  const start = PREFERRED_START.map((key) => fields.find((field) => field.field === key)).find(Boolean) ?? fields[0];
  if (!start) return null;
  const op = start.operators[0] ?? 'is';
  const value = defaultValueFor(start, op);
  return { field: start.field, op, ...(value !== undefined ? { value } : {}) };
}

interface GroupEditorProps {
  group: FilterGroup;
  offered: FilterFieldMeta[];
  grouped: Array<[string, FilterFieldMeta[]]>;
  fieldsByKey: Map<string, FilterFieldMeta>;
  onChange: (next: FilterGroup) => void;
  onRemove?: () => void;
  depth: number;
  flagIncomplete: boolean;
}

function GroupEditor({
  group,
  offered,
  grouped,
  fieldsByKey,
  onChange,
  onRemove,
  depth,
  flagIncomplete,
}: GroupEditorProps) {
  const setNode = (index: number, node: FilterNode) => {
    // A group whose last condition was just removed goes with it. Left in
    // place it is `{ nodes: [] }`, which the server refuses as invalid — the
    // whole list used to turn into an error the moment it happened.
    if (isFilterGroup(node) && node.nodes.length === 0) {
      removeNode(index);
      return;
    }
    const nodes = [...group.nodes];
    nodes[index] = node;
    onChange({ ...group, nodes });
  };

  const removeNode = (index: number) => {
    onChange({ ...group, nodes: group.nodes.filter((_, i) => i !== index) });
  };

  const addCondition = () => {
    const condition = newCondition(offered);
    if (!condition) return;
    onChange({ ...group, nodes: [...group.nodes, condition] });
  };

  const addGroup = () => {
    const condition = newCondition(offered);
    if (!condition) return;
    onChange({
      ...group,
      nodes: [
        ...group.nodes,
        {
          // A nested group defaults to the other logic — the only reason to
          // nest is to say "any of these" inside "all of these", or the reverse.
          logic: group.logic === 'and' ? 'or' : 'and',
          nodes: [condition],
        },
      ],
    });
  };

  return (
    <div className={cn(depth > 0 && 'rounded-control border border-dashed border-line-strong bg-sunken/50 p-2')}>
      {group.nodes.length === 0 && depth === 0 && (
        <p className="py-1 text-body text-ink-muted">No conditions yet. Add one to narrow the list.</p>
      )}

      {group.nodes.map((node, index) => (
        <div key={index} className="flex items-start gap-2 py-1">
          {/* The conjunction sits between rows, and is editable on the second
              row only — repeating a dropdown on every row implies each pair can
              differ, which is not how a group works. */}
          <div className="w-14 shrink-0 pt-1">
            {index === 0 ? (
              <span className="block pt-0.5 text-mini text-ink-subtle">Where</span>
            ) : index === 1 ? (
              <Select
                size="sm"
                value={group.logic}
                onChange={(event) => onChange({ ...group, logic: event.target.value as 'and' | 'or' })}
                aria-label="Combine conditions with"
                className="w-full pe-5 ps-1.5"
              >
                <option value="and">and</option>
                <option value="or">or</option>
              </Select>
            ) : (
              <span className="block pt-0.5 text-center text-mini text-ink-subtle">{group.logic}</span>
            )}
          </div>

          <div className="min-w-0 flex-1">
            {isFilterGroup(node) ? (
              <GroupEditor
                group={node}
                offered={offered}
                grouped={grouped}
                fieldsByKey={fieldsByKey}
                onChange={(next) => setNode(index, next)}
                onRemove={() => removeNode(index)}
                depth={depth + 1}
                flagIncomplete={flagIncomplete}
              />
            ) : (
              <ConditionEditor
                condition={node}
                grouped={grouped}
                fieldsByKey={fieldsByKey}
                onChange={(next) => setNode(index, next)}
                flagIncomplete={flagIncomplete}
              />
            )}
          </div>

          {!isFilterGroup(node) && (
            <Button
              size="icon"
              variant="ghost"
              onClick={() => removeNode(index)}
              aria-label="Remove this condition"
              className="size-7 shrink-0 text-ink-subtle hover:text-critical-ink"
            >
              <Trash2 aria-hidden className="size-3.5" />
            </Button>
          )}
        </div>
      ))}

      <div className={cn('mt-1 flex flex-wrap items-center gap-1.5', group.nodes.length > 0 && 'ps-16')}>
        <Button size="sm" variant="ghost" onClick={addCondition}>
          <Plus aria-hidden className="size-3.5" />
          Condition
        </Button>
        {/* The compiler bounds nesting at five levels; stopping at three keeps
            the interface comprehensible well before the limit bites. */}
        {depth < 2 && (
          <Button size="sm" variant="ghost" onClick={addGroup}>
            <CornerDownRight aria-hidden className="size-3.5 rtl:-scale-x-100" />
            Group
          </Button>
        )}
        {onRemove && (
          <Button size="sm" variant="ghost" onClick={onRemove} className="ms-auto text-ink-subtle">
            Remove group
          </Button>
        )}
      </div>
    </div>
  );
}

function ConditionEditor({
  condition,
  grouped,
  fieldsByKey,
  onChange,
  flagIncomplete,
}: {
  condition: FilterCondition;
  grouped: Array<[string, FilterFieldMeta[]]>;
  fieldsByKey: Map<string, FilterFieldMeta>;
  onChange: (next: FilterCondition) => void;
  flagIncomplete: boolean;
}) {
  const hintId = useId();
  const meta = fieldsByKey.get(condition.field);
  const offeredHere = grouped.some(([, list]) => list.some((field) => field.field === condition.field));
  const incomplete = flagIncomplete && (!meta || !isConditionComplete(condition, meta));

  const changeField = (fieldKey: string) => {
    const next = fieldsByKey.get(fieldKey);
    if (!next) return;
    // A new field usually means a new operator and value too; keeping the old
    // ones would produce a condition the server refuses.
    const op = next.operators[0] ?? 'is';
    const value = defaultValueFor(next, op);
    onChange({ field: fieldKey, op, ...(value !== undefined ? { value } : {}) });
  };

  const changeOperator = (op: FilterOperator) => {
    if (VALUELESS.includes(op)) {
      onChange({ field: condition.field, op });
      return;
    }
    const value = carryValue(condition.value, condition.op, op);
    const fallback = value === undefined ? defaultValueFor(meta, op) : value;
    onChange({ field: condition.field, op, ...(fallback !== undefined ? { value: fallback } : {}) });
  };

  return (
    <div>
      <div
        className={cn(
          'flex flex-wrap items-center gap-1.5 rounded-control',
          incomplete && 'outline outline-1 outline-offset-2 outline-caution',
        )}
      >
        <Select
          size="sm"
          value={condition.field}
          onChange={(event) => changeField(event.target.value)}
          aria-label="Field"
          aria-invalid={flagIncomplete && !meta ? true : undefined}
          className="w-auto max-w-56 min-w-36"
        >
          {/* A field the server no longer offers, or this screen does not, is
              still shown as what it is rather than silently becoming another. */}
          {!offeredHere && <option value={condition.field}>{meta?.label ?? condition.field}</option>}
          {grouped.map(([groupName, groupFields]) => (
            <optgroup key={groupName} label={groupName}>
              {groupFields.map((field) => (
                <option key={field.field} value={field.field}>
                  {field.label}
                </option>
              ))}
            </optgroup>
          ))}
        </Select>

        <Select
          size="sm"
          value={condition.op}
          onChange={(event) => changeOperator(event.target.value as FilterOperator)}
          aria-label="Operator"
          className="w-auto min-w-28"
          disabled={!meta}
        >
          {(meta?.operators ?? [condition.op]).map((op) => (
            <option key={op} value={op}>
              {OPERATOR_LABELS[op] ?? op}
            </option>
          ))}
        </Select>

        {!VALUELESS.includes(condition.op) && meta && (
          <ValueEditor
            meta={meta}
            operator={condition.op}
            value={condition.value}
            onChange={(value) => onChange({ ...condition, ...(value === undefined ? { value: undefined } : { value }) })}
            invalid={incomplete}
            {...(incomplete ? { describedBy: hintId } : {})}
          />
        )}
      </div>
      {incomplete && (
        <p id={hintId} className="mt-1 text-mini text-caution-ink">
          {!meta ? 'This field is no longer available. Remove this condition.' : 'Choose a value, or remove this condition.'}
        </p>
      )}
    </div>
  );
}

/**
 * Keeps what can be kept when the operator changes: one value becomes a list
 * of one, a list becomes its first value, and a pair starts empty.
 */
function carryValue(value: unknown, from: FilterOperator, to: FilterOperator): unknown {
  const wasList = LIST_OPERATORS.includes(from);
  const isList = LIST_OPERATORS.includes(to);
  if (to === 'between') return from === 'between' ? value : ['', ''];
  if (from === 'between') return undefined;
  if (wasList === isList) return value;
  if (isList) {
    if (value === undefined || value === null || value === '') return undefined;
    return [value];
  }
  return Array.isArray(value) ? value[0] : value === CURRENT_ACTOR_GROUPS_TOKEN ? undefined : value;
}

// ---------------------------------------------------------------------------
// Reading a condition back
// ---------------------------------------------------------------------------

/** Renders one condition as human-readable text, for the active-filter chips. */
export function describeCondition(
  condition: FilterCondition,
  fieldsByKey: Map<string, FilterFieldMeta>,
  labelReference?: ReferenceLabeller,
): string {
  const meta = fieldsByKey.get(condition.field);
  const label = meta?.label ?? condition.field;
  const operator = OPERATOR_LABELS[condition.op] ?? condition.op;

  if (VALUELESS.includes(condition.op)) return `${label} ${operator}`;

  const value = condition.value;
  if (condition.op === 'between' && Array.isArray(value) && value.length === 2) {
    return `${label} ${operator} ${readValue(meta, value[0], labelReference)} and ${readValue(meta, value[1], labelReference)}`;
  }
  const readable = Array.isArray(value)
    ? value.map((item) => readValue(meta, item, labelReference)).join(', ')
    : readValue(meta, value, labelReference);

  return `${label} ${operator} ${readable}`.trim();
}

function readValue(meta: FilterFieldMeta | undefined, value: unknown, labelReference?: ReferenceLabeller): string {
  if (value === undefined || value === null || value === '') return '…';
  if (value && typeof value === 'object') {
    if ('relative' in value) return (RELATIVE_LABELS[String((value as { relative: string }).relative)] ?? '').toLowerCase();
    return describeRolling(value) ?? '…';
  }
  if (meta?.type === 'boolean') return value === true || value === 'true' ? 'Yes' : 'No';
  if (value === CURRENT_ACTOR_TOKEN) return labelReference?.(meta?.references, value) ?? 'me';
  if (value === CURRENT_ACTOR_GROUPS_TOKEN) return labelReference?.(meta?.references, value) ?? 'my groups';
  if (meta?.type === 'reference') {
    const name = labelReference?.(meta.references, value);
    if (name) return name;
  }
  const choice = meta?.choices?.find((item) => String(item.value) === String(value));
  if (choice) return choice.label;
  if ((meta?.type === 'date' || meta?.type === 'datetime') && typeof value === 'string') return shortDay(value);
  if (meta?.type === 'duration') return `${Number(value) / 60} min`;
  return String(value);
}

/** "6 Oct 2026" for a date picked in the builder; anything unparseable as it stands. */
function shortDay(value: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}
